/**
 * <lazy-video> — a reusable autoplay-video primitive.
 *
 * Markup contract:
 *   <lazy-video>
 *     <span class="lazy-video__stage"></span>   -- real <video> is cloned in here once visible
 *     <img class="lazy-video__poster" ...>       -- optional, shown until the video is playing
 *     <button class="lazy-video__mute" aria-pressed="true">...</button>  -- optional
 *     <template><video class="..." muted loop playsinline>...</video></template>
 *   </lazy-video>
 *
 * Behavior:
 * - Does not touch the network until the element is near the viewport
 *   (IntersectionObserver, matching this theme's existing lazy-load convention).
 * - Pauses playback when scrolled out of view and resumes when back in view,
 *   instead of looping forever off-screen.
 * - If the <video> node is later moved elsewhere in the DOM (e.g. into a
 *   lightbox) and moved back, a MutationObserver re-syncs the loaded/poster
 *   state automatically — no coordination needed from the code that moves it.
 * - At most `MAX_CONCURRENT_PLAYING` clips decode+play at once across all
 *   <lazy-video> instances on the page. Grids with many videos (e.g. the
 *   social proof wall) used to let every intersecting clip play
 *   simultaneously, which spiked main-thread/decoder work during scroll.
 *   Instances beyond the cap fall back to their poster image and start
 *   playing automatically once a slot frees up (another clip scrolls out
 *   or is paused).
 */
if (!customElements.get('lazy-video')) {
  const MAX_CONCURRENT_PLAYING = 2;
  // Shared across every <lazy-video> instance on the page, in play order
  // (oldest first), so we can evict the longest-playing clip when a new
  // one wants a slot.
  const playingInstances = [];

  customElements.define(
    'lazy-video',
    class LazyVideo extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.lazy-video__stage');
        this.template = this.querySelector('template');
        this.muteButton = this.querySelector('.lazy-video__mute');
        this.video = null;
        this.pendingPlay = false;

        if (!this.stage || !this.template) return;

        if (this.muteButton) {
          this.muteButton.addEventListener('click', this.onMuteToggle.bind(this));
        }

        this.stageObserver = new MutationObserver(() => {
          this.classList.toggle('lazy-video--attached', this.stage.contains(this.video));
        });
        this.stageObserver.observe(this.stage, { childList: true });

        // A tighter margin than a plain image lazy-load: video decode is
        // far more expensive than decoding a poster, so clips shouldn't
        // start loading/playing until they're much closer to view.
        this.intersectionObserver = new IntersectionObserver(this.onIntersect.bind(this), {
          rootMargin: '50px 0px',
          threshold: 0.01,
        });
        this.intersectionObserver.observe(this);
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.stageObserver?.disconnect();
        this.releasePlaySlot();
      }

      onIntersect(entries) {
        const entry = entries[entries.length - 1];
        if (entry.isIntersecting) {
          this.load();
          this.requestPlaySlot();
        } else {
          this.pendingPlay = false;
          if (this.stage.contains(this.video)) this.video?.pause();
          this.releasePlaySlot();
        }
      }

      // Claims a playback slot if one is free; otherwise evicts the
      // instance that has been playing longest so the clip that just
      // scrolled into view still gets to play (matches user expectation
      // that "what's on screen now" takes priority over what scrolled
      // past a moment ago).
      requestPlaySlot() {
        if (playingInstances.includes(this)) {
          this.playIfLoaded();
          return;
        }

        if (playingInstances.length >= MAX_CONCURRENT_PLAYING) {
          const evicted = playingInstances.shift();
          if (evicted && evicted !== this) {
            evicted.pendingPlay = false;
            if (evicted.stage?.contains(evicted.video)) evicted.video?.pause();
          }
        }

        playingInstances.push(this);
        this.playIfLoaded();
      }

      releasePlaySlot() {
        const index = playingInstances.indexOf(this);
        if (index !== -1) playingInstances.splice(index, 1);
      }

      playIfLoaded() {
        if (!this.video) {
          this.pendingPlay = true;
          return;
        }
        this.pendingPlay = false;
        if (this.stage.contains(this.video)) this.video.play().catch(() => {});
      }

      load() {
        if (this.video) return;
        const fragment = this.template.content.cloneNode(true);
        const video = fragment.querySelector('video');
        if (!video) return;

        this.video = video;
        this.stage.appendChild(fragment);
        this.classList.add('lazy-video--loaded');

        video.addEventListener('playing', () => this.classList.add('lazy-video--playing'));
        video.addEventListener('pause', () => this.classList.remove('lazy-video--playing'));

        if (this.pendingPlay) this.playIfLoaded();
      }

      onMuteToggle(event) {
        event.stopPropagation();
        if (!this.video) return;
        this.video.muted = !this.video.muted;
        // aria-pressed tracks the actual muted state (true = currently
        // muted), which is what the icon-swap CSS keys off of.
        this.muteButton.setAttribute('aria-pressed', String(this.video.muted));
      }
    }
  );
}
